import { inject, Injectable } from "@angular/core";

import { CountriesApiService } from "@components/shared/countries/api";
import { RiskAssessmentTemplateResourceType } from "@components/shared/risk-assessment-templates/constants";
import { TextConstants } from "@shared/constants";
import {
  ConnectionsService,
  DeliveriesService,
  LocationsService,
  MaterialsService,
  ProductsService,
} from "@shared/services";
import { CommonUtils } from "@shared/utils";

import { RiskAssessmentReport, RiskAssessmentReportRecord } from "../models";

type EntityService =
  | CountriesApiService
  | DeliveriesService
  | LocationsService
  | MaterialsService
  | ConnectionsService
  | ProductsService;

@Injectable({ providedIn: "root" })
export class RiskAssessmentReportsRecordService {
  private readonly countriesApi = inject(CountriesApiService);

  private readonly deliveriesService = inject(DeliveriesService);

  private readonly locationsService = inject(LocationsService);

  private readonly materialsService = inject(MaterialsService);

  private readonly connectionsService = inject(ConnectionsService);

  private readonly productsService = inject(ProductsService);

  private readonly serviceConfigMap: Record<
    RiskAssessmentTemplateResourceType,
    { service: EntityService; fields: string[] }
  > = {
    [RiskAssessmentTemplateResourceType.COUNTRY]: {
      service: this.countriesApi,
      fields: ["name", "code"],
    },
    [RiskAssessmentTemplateResourceType.DELIVERY]: {
      service: this.deliveriesService,
      fields: ["deliveryId"],
    },
    [RiskAssessmentTemplateResourceType.LOCATION]: {
      service: this.locationsService,
      fields: ["name"],
    },
    [RiskAssessmentTemplateResourceType.MATERIAL]: {
      service: this.materialsService,
      fields: ["name"],
    },
    [RiskAssessmentTemplateResourceType.ORGANISATION]: {
      service: this.connectionsService,
      fields: ["name"],
    },
    [RiskAssessmentTemplateResourceType.PRODUCT]: {
      service: this.productsService,
      fields: ["name"],
    },
  };

  async get(
    reports: RiskAssessmentReport[],
    type: RiskAssessmentTemplateResourceType,
  ): Promise<RiskAssessmentReportRecord[]> {
    if (!reports.length) {
      return [];
    }

    // only one record stored against report
    const ids = reports.map(({ recordUris }) => CommonUtils.getUriId(recordUris[0]));

    if (!ids.length) {
      return [];
    }

    const config = this.serviceConfigMap[type];

    if (!config) {
      throw new Error(TextConstants.UNSUPPORTED_RESOURCE_TYPE);
    }

    const data = await config.service.getByIdsGraphQL(ids);

    const dataMap = new Map<string, any>(
      data.map((item): [string, any] => [
        item.id,
        Object.fromEntries(
          config.fields.map((field): [string, any] => {
            if (field === "deliveryId") {
              return ["name", item[field]];
            }

            return [field, item[field] || undefined];
          }),
        ),
      ]),
    );

    return reports.map((report) => {
      const id = CommonUtils.getUriId(report.recordUris[0]);
      const { name, code } = dataMap.get(id);

      const record: RiskAssessmentReportRecord["record"] = {
        id,
        name,
        type,
        ...(type === RiskAssessmentTemplateResourceType.COUNTRY ? { code } : {}),
      };

      return {
        ...report,
        record,
      };
    });
  }
}
